import { z } from 'zod';

import { PickedCivilDayDto } from './civilDate';
import { ApiLockerKeyCurrentAssignment } from './lockerKeys';
import type { MembershipRenewalReason } from '../utils/membershipRenewal';

export const StaffHomeDayQueryDto = z
  .object({
    date: PickedCivilDayDto,
    search: z.string().trim().max(255).optional(),
    offset: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    // Powers the "Arrivals" tab — same client list, filtered to only those
    // with a check-in on `date`. Omitted/false keeps the "All clients" shape.
    onlyCheckedIn: z.coerce.boolean().optional(),
  })
  .strict();

export type ApiStaffHomeDayQueryDto = z.infer<typeof StaffHomeDayQueryDto>;

export enum StaffHomePaymentStatus {
  PAID = 'PAID',
  DUE = 'DUE',
  NONE = 'NONE',
  UNKNOWN = 'UNKNOWN',
}

export interface ApiStaffHomeClientRow {
  clientId: string;
  name: string;
  /** Celina 2.9 — the client's member number in this organization (an int;
   * shown padded, `formatMemberNumber`). Null when none was assigned. */
  memberNumber: number | null;
  userMembershipId: string | null;
  membershipName: string | null;
  activityStatus: string;
  /** Whether the check-in endpoint will currently accept this client's
   * membership — the raw `activityStatus` alone lags the civil date window
   * and freeze state (backend `isCheckInEligible`), so this must gate the
   * Check-in button rather than `activityStatus`. */
  checkInEligible: boolean;
  paymentStatus: StaffHomePaymentStatus;
  membershipEndDate: string | null;
  lastPaymentDate: string | null;
  trainerName: string | null;
  checkedInAt: string | null;
  /** The day's arrival was marked "probni trening" (trial workout). */
  checkedInIsTrial: boolean;
}

export interface ApiStaffHomeMembershipTypeCount {
  membershipName: string;
  count: number;
}

export interface ApiCurrencyTotal {
  currency: string | null;
  sum: number;
}

export interface ApiStaffHomeProductSold {
  productId: string;
  currency: string;
  productName: string;
  quantity: number;
  sum: number;
}

export interface ApiStaffHomeDayRevenue {
  totals: ApiCurrencyTotal[];
  count: number;
}

export interface ApiStaffHomeDayStats {
  checkInsCount: number;
  bookingSessionsCount: number;
  // Booking walk-in payments + Desk sales only (decision: memory
  // `project_staff_role_feature.md` #10's boundary — check-ins carry no
  // price of their own, so they stay a plain count above, never money).
  revenue: ApiStaffHomeDayRevenue;
  membershipTypeBreakdown: ApiStaffHomeMembershipTypeCount[];
  productsSold: ApiStaffHomeProductSold[];
}

export interface ApiStaffHomeDayResponse {
  data: ApiStaffHomeClientRow[];
  total: number;
  stats: ApiStaffHomeDayStats;
}

export const StaffHomeArrivalsQueryDto = z
  .object({
    date: PickedCivilDayDto,
    search: z.string().trim().max(255).optional(),
    offset: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict();

export type ApiStaffHomeArrivalsQueryDto = z.infer<
  typeof StaffHomeArrivalsQueryDto
>;

/** How the person came in. `isTrial` is orthogonal: a trial visit still
 * arrived through one of these. */
export enum StaffHomeArrivalSource {
  /** Desk check-in of an account holder. */
  DESK = 'DESK',
  /** An account holder's seat on a booked session. */
  BOOKING = 'BOOKING',
  /** Workout completed with a trainer (client's "Done Workout" feedback). */
  TRAINER_WORKOUT = 'TRAINER_WORKOUT',
  /** Daily ticket sold to a person without an account. */
  DAY_PASS = 'DAY_PASS',
  /** Person without an account holding a seat on a group session. */
  WALK_IN = 'WALK_IN',
}

export interface ApiStaffHomeArrivalRow {
  /** Unique across sources (`<source>:<row id>`). */
  id: string;
  source: StaffHomeArrivalSource;
  /** ISO instant the arrival is ordered by. */
  arrivedAt: string;
  /** Set for an account holder, null for a prospect. */
  clientId: string | null;
  prospectId: string | null;
  name: string;
  /**
   * Celina 2.9 — the member number of the row's account (`clientId`) as a
   * CLIENT of this organization; null for a row without an account and for an
   * account that is not a numbered client here.
   */
  memberNumber: number | null;
  hasAccount: boolean;
  isTrial: boolean;
  /** The membership the visit was deducted from, when one was. */
  userMembershipId: string | null;
  membershipName: string | null;
  /** False for a desk check-in recorded without spending a visit. */
  consumesVisit: boolean;
  /** Session title for BOOKING / WALK_IN, null otherwise. */
  eventTitle: string | null;
  authorName: string | null;
  voidedAt: string | null;
  voidedByName: string | null;
  voidReason: string | null;
  /**
   * Celina 1.1 — the account holder's current membership is not paid in full
   * (a first assignment counts), whatever day the log shows. Flag only, no
   * amount; false for people without an account.
   */
  notPaid: boolean;
  /**
   * Celina 1.8 — the account holder's CURRENT membership is up for renewal
   * TODAY (organization day), by the membership card's own rule
   * (`membershipRenewalState`: expired date or used-up visits; one-day
   * tickets, card-managed, frozen, ended-early and already-renewed cycles
   * excluded). Informational, independent of the log's date and of the money
   * right; null for people without an account and when nothing is due.
   */
  renewal: {
    userMembershipId: string;
    reason: MembershipRenewalReason;
  } | null;
  /**
   * The person's live locker key NOW (whatever the log's date or void state),
   * null when none. Present only for a viewer who may manage locker keys in a
   * configured organization; absent otherwise.
   */
  lockerKey?: ApiLockerKeyCurrentAssignment | null;
  /** Issue offered: configured groups, non-voided row, holder eligible now. */
  lockerKeyCanIssue?: boolean;
}

export interface ApiStaffHomeArrivalsResponse {
  data: ApiStaffHomeArrivalRow[];
  total: number;
  /**
   * Locker-key metadata, present only for a viewer who may manage locker
   * keys — it drives the "Current key" column even on an empty page.
   */
  lockerKeysConfigured?: boolean;
  /** ISO instant the row `lockerKey` fields were evaluated at. */
  asOf?: string;
}
